import { apiClient } from './api-client';
import type {
  Tenant,
  CreateTenantDto,
  UpdateTenantDto,
  Subscription,
  CreateSubscriptionDto,
  UpdateSubscriptionDto,
  SubscriptionLimits,
  TenantSettings,
  BusinessHours,
  Branding,
  TenantFeatures,
  TenantUsage,
  PaginatedTenants,
} from '@/types/tenant';

export interface TenantFilters {
  page?: number;
  limit?: number;
  search?: string;
}

export const tenantService = {
  // Tenant CRUD
  async createTenant(data: CreateTenantDto): Promise<Tenant> {
    const response = await apiClient.post('/tenants', data);
    return response.data;
  },

  async getTenants(filters: TenantFilters = {}): Promise<PaginatedTenants> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);

    const response = await apiClient.get(`/tenants?${params.toString()}`);
    return response.data;
  },

  async getTenant(id: string): Promise<Tenant> {
    const response = await apiClient.get(`/tenants/${id}`);
    return response.data;
  },

  async getTenantBySlug(slug: string): Promise<Tenant> {
    const response = await apiClient.get(`/tenants/slug/${slug}`);
    return response.data;
  },

  async updateTenant(id: string, data: UpdateTenantDto): Promise<Tenant> {
    const response = await apiClient.put(`/tenants/${id}`, data);
    return response.data;
  },

  async deleteTenant(id: string): Promise<void> {
    await apiClient.delete(`/tenants/${id}`);
  },

  async getMyOrganization(): Promise<any> {
    const response = await apiClient.get('/tenants/my-organization');
    return response.data;
  },

  async updateMyOrganization(data: Partial<UpdateTenantDto>): Promise<any> {
    const response = await apiClient.put('/tenants/my-organization', data);
    return response.data;
  },

  async activateTenant(id: string): Promise<Tenant> {
    const response = await apiClient.post(`/tenants/${id}/activate`);
    return response.data;
  },

  async deactivateTenant(id: string): Promise<Tenant> {
    const response = await apiClient.post(`/tenants/${id}/deactivate`);
    return response.data;
  },

  async getTenantUsage(id: string): Promise<TenantUsage> {
    const response = await apiClient.get(`/tenants/${id}/usage`);
    return response.data;
  },

  // Tenant Settings
  async getTenantSettings(tenantId: string): Promise<TenantSettings> {
    const response = await apiClient.get(`/tenants/${tenantId}/settings`);
    return response.data;
  },

  async updateTenantSettings(
    tenantId: string,
    data: Partial<TenantSettings>,
  ): Promise<TenantSettings> {
    const response = await apiClient.put(`/tenants/${tenantId}/settings`, data);
    return response.data;
  },

  async getBusinessHours(tenantId: string): Promise<BusinessHours> {
    const response = await apiClient.get(`/tenants/${tenantId}/settings/business-hours`);
    return response.data;
  },

  async updateBusinessHours(tenantId: string, data: BusinessHours): Promise<BusinessHours> {
    const response = await apiClient.put(`/tenants/${tenantId}/settings/business-hours`, data);
    return response.data;
  },

  async getBranding(tenantId: string): Promise<Branding> {
    const response = await apiClient.get(`/tenants/${tenantId}/settings/branding`);
    return response.data;
  },

  async updateBranding(tenantId: string, data: Branding): Promise<Branding> {
    const response = await apiClient.put(`/tenants/${tenantId}/settings/branding`, data);
    return response.data;
  },

  async getFeatures(tenantId: string): Promise<TenantFeatures> {
    const response = await apiClient.get(`/tenants/${tenantId}/settings/features`);
    return response.data;
  },

  async updateFeatures(tenantId: string, data: TenantFeatures): Promise<TenantFeatures> {
    const response = await apiClient.put(`/tenants/${tenantId}/settings/features`, data);
    return response.data;
  },

  // Subscriptions
  async createSubscription(data: CreateSubscriptionDto): Promise<Subscription> {
    const response = await apiClient.post('/subscriptions', data);
    return response.data;
  },

  async getSubscription(id: string): Promise<Subscription> {
    const response = await apiClient.get(`/subscriptions/${id}`);
    return response.data;
  },

  async getTenantSubscription(tenantId: string): Promise<Subscription> {
    const response = await apiClient.get(`/subscriptions/tenant/${tenantId}`);
    return response.data;
  },

  async updateSubscription(id: string, data: UpdateSubscriptionDto): Promise<Subscription> {
    const response = await apiClient.put(`/subscriptions/${id}`, data);
    return response.data;
  },

  async cancelSubscription(id: string): Promise<Subscription> {
    const response = await apiClient.post(`/subscriptions/${id}/cancel`);
    return response.data;
  },

  async reactivateSubscription(id: string): Promise<Subscription> {
    const response = await apiClient.post(`/subscriptions/${id}/reactivate`);
    return response.data;
  },

  async upgradeSubscription(id: string, plan: string): Promise<Subscription> {
    const response = await apiClient.post(`/subscriptions/${id}/upgrade`, { plan });
    return response.data;
  },

  async downgradeSubscription(id: string, plan: string): Promise<Subscription> {
    const response = await apiClient.post(`/subscriptions/${id}/downgrade`, { plan });
    return response.data;
  },

  async getSubscriptionLimits(id: string): Promise<SubscriptionLimits> {
    const response = await apiClient.get(`/subscriptions/${id}/limits`);
    return response.data;
  },
};

export default tenantService;
